import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Container, Typography, TextField, Button, Box, Alert, MenuItem,
  InputAdornment, IconButton, Paper, Fade, Grow, CircularProgress
} from '@mui/material';
import api from '../services/api';
import PhoneNumberField from '../components/PhoneNumberField';
import {   
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Wc as WcIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const RegisterPage = ({ keywordsData, language }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    age: '',
    sex: '',
    user_phone: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [serverError, setServerError] = useState('');
  const navigate = useNavigate();

  // Validation functions
  const validateField = (field, value, allValues = formData) => {
    switch (field) {
      case 'username':
        if (!value) return language === 'ar' ? 'إسم المستخدم مطلوب' : 'Username required';
        if (value.length < 3) return language === 'ar' ? 'اسم المستخدم يجب أن يتكون من 3 أحرف على الأقل' : 'Username must be at least 3 characters';
        if (!/^[a-zA-Z0-9_]+$/.test(value)) {
          return language === 'ar' ? 'يجب أن يحتوي اسم المستخدم فقط على أحرف وأرقام وشرطات سفلية' : 'Username can only contain letters, numbers, and underscores';
        }
        return '';

      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) return language === 'ar' ? 'الإيميل مطلوب' : 'Email required';
        if (!emailRegex.test(value)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
        return '';

      case 'password':
        if (!value) return language === 'ar' ? 'كلمة المرور مطلوبة' : 'Password required';
        if (value.length < 6) return language === 'ar' ? 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل' : 'Password must be at least 6 characters';
        
        let strength = 0;
        if (value.length >= 8) strength++;
        if (/(?=.*[a-z])/.test(value)) strength++;
        if (/(?=.*[A-Z])/.test(value)) strength++;
        if (/(?=.*\d)/.test(value)) strength++;
        if (/(?=.*[@$!%*?&])/.test(value)) strength++;
        
        if (strength !== passwordStrength) {
          setTimeout(() => setPasswordStrength(strength), 0);
        }
        
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          return language === 'ar' ? 'يجب أن تحتوي كلمة المرور على أحرف كبيرة وصغيرة وأرقام' : 'Password must contain uppercase, lowercase, and numbers';
        }
        return '';

      case 'confirmPassword':
        if (!value) return language === 'ar' ? 'الرجاء تأكيد كلمة المرور' : 'Please confirm your password';
        if (value !== allValues.password) return language === 'ar' ? 'كلمة المرور غير متطابقة' : 'Passwords do not match';
        return '';

      case 'age':
        if (!value) return language === 'ar' ? 'العمر مطلوب' : 'Age required';
        const ageNum = parseInt(value);
        if (isNaN(ageNum) || ageNum < 1 || ageNum > 120) return language === 'ar' ? 'الرجاء إدخال عمر صالح (1-120)' : 'Please enter a valid age (1-120)';
        return '';

      case 'sex':
        if (!value) return language === 'ar' ? 'نوع الجنس مطلوب' : 'Gender required';
        return '';

      case 'user_phone':
        if (!value) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number required';
        const cleanPhone = value.replace(/[^\d+]/g, '');
        const digitsOnly = cleanPhone.replace(/\+/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) {
          return language === 'ar' ? 'رقم الهاتف يجب أن يحتوي على 7-15 رقم' : 'Phone number must contain 7-15 digits';
        }
        return '';

      default:
        return '';
    }
  };

  // Validate a single field and update errors
  const validateSingleField = (field, value) => {
    const error = validateField(field, value, formData);
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  // Handle change for regular text fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear server error when user starts typing again
    if (serverError) setServerError('');
    
    // Update form data
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Mark field as touched on first change
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
    
    // Validate the field immediately
    setTimeout(() => {
      validateSingleField(name, value);
    }, 0);
  };

  // Handle phone number change
  const handlePhoneChange = (value) => {
    // Clear server error when user starts typing again
    if (serverError) setServerError('');
    
    setFormData(prev => ({ ...prev, user_phone: value }));
    
    if (!touched.user_phone) {
      setTouched(prev => ({ ...prev, user_phone: true }));
    }
    
    setTimeout(() => {
      validateSingleField('user_phone', value);
    }, 0);
  };

  // Handle blur event
  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateSingleField(field, formData[field]);
  };

  // Check if form is valid (real-time)
  const isFormValid = () => {
    // Check if all fields have values
    const allFieldsFilled = Object.values(formData).every(value => value !== '');
    if (!allFieldsFilled) return false;
    
    // Check if there are any validation errors
    const hasValidationErrors = Object.values(errors).some(error => error);
    
    // Don't disable based on server error - server error is shown but doesn't block resubmission
    return !hasValidationErrors;
  };

  // Get password strength color and text
  const getPasswordStrength = () => {
    const strengthMap = {
      0: { color: '#FF3D00', text: language === 'ar' ? 'ضعيفة جداً' : 'Very Weak', width: '20%' },
      1: { color: '#FF9800', text: language === 'ar' ? 'ضعيفة' : 'Weak', width: '40%' },
      2: { color: '#FFC107', text: language === 'ar' ? 'متوسطة' : 'Medium', width: '60%' },
      3: { color: '#4CAF50', text: language === 'ar' ? 'قوية' : 'Strong', width: '80%' },
      4: { color: '#2E7D32', text: language === 'ar' ? 'قوية جداً' : 'Very Strong', width: '100%' },
    };
    return strengthMap[passwordStrength] || strengthMap[0];
  };

  // Clear specific field error
  const clearFieldError = (field) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous server error
    setServerError('');
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(field => {
      allTouched[field] = true;
    });
    setTouched(allTouched);
    
    // Validate all fields
    let hasError = false;
    const newErrors = {};
    
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field], formData);
      if (error) {
        hasError = true;
        newErrors[field] = error;
      }
    });
    
    if (hasError) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await api.post('/auth/register', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        user_phone: formData.user_phone,
        age: formData.age,
        sex: formData.sex
      });
      
      navigate('/login', { 
        state: { 
          message: language === 'ar' ? 'تم التسجيل بنجاح، الرجاء تسجيل الدخول' : 'Registration successful, please login',
          severity: 'success'
        } 
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || (language === 'ar' ? 'فشل التسجيل' : 'Registration failed');
      
      // Check if it's a phone number or email already exists error
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes('phone') || errorLower.includes('رقم الهاتف')) {
        setErrors(prev => ({ ...prev, user_phone: errorMessage }));
        setTouched(prev => ({ ...prev, user_phone: true }));
      } else if (errorLower.includes('email') || errorLower.includes('الإيميل')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
        setTouched(prev => ({ ...prev, email: true }));
      } else if (errorLower.includes('username') || errorLower.includes('إسم المستخدم')) {
        setErrors(prev => ({ ...prev, username: errorMessage }));
        setTouched(prev => ({ ...prev, username: true }));
      } else {
        setServerError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grow in timeout={500}>
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 }, mb: 10 }}>
        <Paper elevation={0} sx={{ 
          p: { xs: 2, sm: 4 }, 
          borderRadius: '24px',
          background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
          border: '1px solid rgba(255, 215, 0, 0.2)'
        }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ 
            color: '#FFD700', 
            fontWeight: 800,
            fontSize: { xs: '1.8rem', sm: '2rem' }
          }}>
            {language === 'ar' ? 'إنشاء حساب' : 'Create Account'}
          </Typography>
          
          <Typography variant="body2" align="center" sx={{ color: '#B3B3B3', mb: 3 }}>
            {language === 'ar' ? 'قم بإنشاء حساب جديد للاستمتاع بخدماتنا' : 'Create a new account to enjoy our services'}
          </Typography>

          {/* Server Error Alert */}
          {serverError && (
            <Fade in>
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: '12px' }}
                onClose={() => setServerError('')}
              >
                {serverError}
              </Alert>
            </Fade>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Username Field */}
            <TextField
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'إسم المستخدم' : 'Username'}
              name="username"
              value={formData.username}
              onChange={handleChange}
              onBlur={handleBlur('username')}
              error={touched.username && !!errors.username}
              helperText={touched.username && errors.username}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: errors.username && touched.username ? '#FF3D00' : '#FFD700' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
              }}
            />
            
            {/* Email Field */}
            <TextField
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur('email')}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: errors.email && touched.email ? '#FF3D00' : '#FFD700' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
              }}
            />
            
            {/* Age Field */}
            <TextField
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'العمر' : 'Age'}
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              onBlur={handleBlur('age')}
              error={touched.age && !!errors.age}
              helperText={touched.age && errors.age}
              inputProps={{ min: 1, max: 120 }}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon sx={{ color: errors.age && touched.age ? '#FF3D00' : '#FFD700' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
              }}
            />
            
            {/* Gender Field */}
            <TextField
              select
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'الجنس' : 'Gender'}
              name="sex"
              value={formData.sex}
              onChange={handleChange}
              onBlur={handleBlur('sex')}
              error={touched.sex && !!errors.sex}
              helperText={touched.sex && errors.sex}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WcIcon sx={{ color: errors.sex && touched.sex ? '#FF3D00' : '#FFD700' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
              }}
            >
              <MenuItem value="">{language === 'ar' ? 'اختر الجنس' : 'Select gender'}</MenuItem>
              <MenuItem value="male">{language === 'ar' ? 'ذكر' : 'Male'}</MenuItem>
              <MenuItem value="female">{language === 'ar' ? 'أنثى' : 'Female'}</MenuItem>
            </TextField>

            {/* Phone Field */}
            <PhoneNumberField
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
              name="user_phone"
              value={formData.user_phone}
              onChange={handlePhoneChange}
              onBlur={handleBlur('user_phone')}
              error={touched.user_phone && !!errors.user_phone}
              helperText={touched.user_phone && errors.user_phone}
              lang={language}
              required
            />
            
            {/* Password Field */}
            <TextField
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'كلمة المرور' : 'Password'}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur('password')}
              error={touched.password && !!errors.password}
              helperText={touched.password && errors.password}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: errors.password && touched.password ? '#FF3D00' : '#FFD700' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#B3B3B3' }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
              }}
            />
            
            {/* Password Strength Indicator */}
            {formData.password && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Box sx={{ 
                  height: 4, 
                  backgroundColor: '#2D2D2D', 
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <Box sx={{ 
                    width: getPasswordStrength().width,
                    height: '100%',
                    backgroundColor: getPasswordStrength().color,
                    transition: 'width 0.3s ease'
                  }} />
                </Box>
                <Typography variant="caption" sx={{ 
                  color: getPasswordStrength().color,
                  mt: 0.5,
                  display: 'block'
                }}>
                  {getPasswordStrength().text}
                </Typography>
              </Box>
            )}
            
            {/* Confirm Password Field */}
            <TextField
              fullWidth
              margin="normal"
              label={language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur('confirmPassword')}
              error={touched.confirmPassword && !!errors.confirmPassword}
              helperText={touched.confirmPassword && errors.confirmPassword}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: errors.confirmPassword && touched.confirmPassword ? '#FF3D00' : '#FFD700' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#B3B3B3' }}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#1A1A1A',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                },
                '& .MuiInputLabel-root': { color: '#B3B3B3' },
                '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
              }}
            />
            
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              disabled={!isFormValid() || isSubmitting}
              sx={{ 
                mt: 4, 
                py: 1.5,
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 700,
                fontSize: '1rem',
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2D2D2D',
                  color: '#6B6B6B'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: '#000000' }} />
              ) : (
                language === 'ar' ? 'إنشاء الحساب' : 'Create Account'
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
              <Button 
                component={Link} 
                to="/login" 
                sx={{ 
                  color: '#FFD700',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#FFE55C'
                  }
                }}
              >
                {language === 'ar' ? 'سجل دخولك من هنا' : 'Login here'}
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Grow>
  );
};

export default RegisterPage;